/**
 * GraphQL documents for the lexicon service (VE-24948 / U5).
 *
 * Lexicons are aiWARE structured-data objects against a published JSON Schema,
 * following the `redactionCodes` / `redactionStatuses` precedent. Every shape
 * here was checked against the real `aiware-core` schema rather than recalled —
 * two facts from that check drive the design:
 *
 *   1. There is NO bulk/batch SDO mutation. Only create/update/delete, one
 *      object per call.
 *   2. `updateStructuredData` replaces `data` WHOLESALE — there is no patch.
 *
 * Together those are why a lexicon's entries are embedded in one SDO (an N-row
 * CSV import is one atomic write, not N un-transacted round trips) and why
 * every write is read-modify-write under a `revision` guard.
 *
 * A third fact, learned the expensive way (VE-29495): `StructuredDataList.count`
 * is the size of the PAGE the server returned — `rows.length`, set in core's
 * own `toPage` — not a total of everything matching the filter, and the SDO API
 * has no count aggregate to ask instead. NO DOCUMENT HERE SELECTS IT. Every
 * caller that took it read it as a total and got a different bug out of it.
 * Count records, or ask for one more than you will show.
 */

/**
 * Resolves a data registry's currently published schema id.
 *
 * This query exists instead of a `lexiconSchemaId` config pin, and the reason is
 * a bug this repo has fixed twice. With a pinned id, a write that failed schema
 * validation re-resolved the SAME stale id on retry, so a genuine schema
 * republish left saves broken until a config redeploy (VE-24546); VE-25957 then
 * removed the pin from all five shipped configs. Config carries the REGISTRY id
 * only — see `schemaResolver.ts`.
 */
export const RESOLVE_LEXICON_SCHEMA_ID = `
  query resolveLexiconSchemaId($dataRegistryId: ID!) {
    dataRegistry(id: $dataRegistryId) {
      id
      publishedSchema {
        id
        majorVersion
        minorVersion
      }
    }
  }
`;

export interface ResolveLexiconSchemaIdResponse {
  dataRegistry?: {
    id: string;
    publishedSchema?: {
      id: string;
      majorVersion: number;
      minorVersion: number;
    } | null;
  } | null;
}

/** Lists lexicons for the caller's org. Org scoping is enforced by the platform. */
/*
 * THE NAME LOOKUP FILTERS ON `nameLower`, NEVER ON `name`.
 *
 * A filter on `name` was tried and removed (review-gate B4): the platform's
 * `filter` is exact-match, so it could not implement BR-2's case-INSENSITIVE
 * uniqueness — it silently accepted "case terms" beside "Case Terms". The
 * replacement was a client-side scan of the whole org, up to 100 sequential
 * queries before a single byte was written (review nit N14).
 *
 * `nameLower` resolves both: the folding happens at write time, once, so the
 * exact-match filter is asking a question it can actually answer. See
 * `lexiconNameKey` — and do not reintroduce a filter on the raw `name`.
 */
export const FETCH_LEXICONS = `
  query fetchLexicons(
    $schemaId: ID!
    $offset: Int
    $limit: Int
    $filter: JSONData
  ) {
    structuredDataObjects(
      schemaId: $schemaId
      offset: $offset
      limit: $limit
      filter: $filter
    ) {
      records {
        id
        createdDateTime
        modifiedDateTime
        data
      }
    }
  }
`;

/**
 * The two list queries: SUMMARIES that are live, and SUMMARIES that are deleted.
 *
 * Both halves of each filter are load-bearing. `recordPart` keeps detail
 * records out — one carries the entries this storage split exists to stop
 * sending — and `isDeleted` is what makes the deleted view a query rather than
 * a client-side sieve over everything the org has.
 *
 * **STRING VALUES, NOT BOOLEANS, and that is not a style choice.** A boolean
 * literal makes the SDO endpoint return `internal_error` — measured by the
 * watermark library, both bare (`isDeleted: false`) and wrapped
 * (`isDeleted: { eq: false }`). Strings are coerced against the stored boolean
 * and filter correctly. `$filter: JSONData` with exact-match string values is
 * the same call `watermarks/services` makes in production (`PICKABLE_FILTER`),
 * not a guess.
 *
 * THE MATCHING IS EXACT, so a record that OMITS either key matches NEITHER
 * `"true"` nor `"false"` and disappears from both lists at once. The watermark
 * library measured that on its own registry: of six rows, three carried neither
 * key and vanished. Two things keep it from happening here, and both must hold:
 * `docs/schemas/LexiconSchema.md` marks both keys REQUIRED, so the platform rejects a
 * record that would become invisible, and every write path stamps both — see
 * `toSummaryData` and `toEntriesData`.
 */
export const LEXICON_SUMMARY_FILTER = {
  recordPart: 'summary',
  isDeleted: 'false',
} as const;

/** The Deleted Lexicons view — the same query with the flag inverted. */
export const LEXICON_DELETED_FILTER = {
  recordPart: 'summary',
  isDeleted: 'true',
} as const;

/**
 * The "(N)" on the "View deleted lexicons" link, and NOTHING else.
 *
 * It asks for ids only. The list query above carries `data` for every record
 * because the table renders it; a count renders none of it, and one page of
 * deleted summaries is the largest response in this module that nobody reads.
 *
 * IT DOES NOT SELECT `count`. `structuredDataObjects.count` is the number of
 * records in the page returned — `rows.length`, server-side — so it is capped
 * by `limit` and can never report a total. Reading it off a `limit: 1` query is
 * exactly what pinned this link at "(1)" (VE-29495). The caller counts
 * `records` instead, and asks for one more than it will show so that "more than
 * we display" is a fact about the response rather than a second query.
 */
export const COUNT_DELETED_LEXICONS = `
  query countDeletedLexicons($schemaId: ID!, $limit: Int, $filter: JSONData) {
    structuredDataObjects(schemaId: $schemaId, limit: $limit, filter: $filter) {
      records {
        id
      }
    }
  }
`;

export interface CountDeletedLexiconsResponse {
  structuredDataObjects?: {
    records: { id: string }[];
  } | null;
}

export interface LexiconSdoRecord {
  id: string;
  createdDateTime: string;
  modifiedDateTime: string;
  data: unknown;
}

export interface FetchLexiconsResponse {
  structuredDataObjects?: {
    records: LexiconSdoRecord[];
  } | null;
}

/**
 * The BR-2 uniqueness probe: LIVE summaries whose folded name matches exactly.
 *
 * A handful rather than one, because the answer is not "does a record exist"
 * but "does a record OTHER THAN THIS ONE exist" — a rename has to find itself
 * and ignore it. A small limit also means a registry that somehow holds
 * duplicates already (written before this field, or by a script) still answers
 * correctly instead of returning only the caller's own row.
 */
export const FETCH_LEXICONS_BY_NAME_KEY = `
  query fetchLexiconsByNameKey($schemaId: ID!, $filter: JSONData) {
    structuredDataObjects(schemaId: $schemaId, limit: 5, filter: $filter) {
      records {
        id
        createdDateTime
        modifiedDateTime
        data
      }
    }
  }
`;

/** The probe's filter. `nameLower` is `lexiconNameKey(name)`. */
export const lexiconNameKeyFilter = (nameLower: string) => ({
  ...LEXICON_SUMMARY_FILTER,
  nameLower,
});

/** Reads one lexicon by id — the re-read half of read-modify-write. */
export const FETCH_LEXICON_BY_ID = `
  query fetchLexiconById($schemaId: ID!, $id: ID!) {
    structuredDataObjects(schemaId: $schemaId, id: $id) {
      records {
        id
        createdDateTime
        modifiedDateTime
        data
      }
    }
  }
`;

/**
 * The operation name a GraphQL document declares.
 *
 * `operationName` is not a label — the server executes the operation with that
 * name IN THE DOCUMENT IT WAS SENT, so a name that is not in the document fails
 * with `Unknown operation named "..."` and a 200 status, which reads as a
 * server problem rather than a caller one. Three writes shipped with invented
 * names (`createLexiconEntries` against the `createLexicon` document, and two
 * more) before that surfaced.
 *
 * Deriving it from the document removes the class: a call site cannot name an
 * operation the document does not declare, because it does not name one at all.
 * Wanting a distinct name in platform logs is a reason to write a distinct
 * DOCUMENT — see `CREATE_LEXICON_ENTRIES` — not to pass a different string.
 */
export function operationNameOf(document: string): string {
  const match = /\b(?:query|mutation)\s+(\w+)/.exec(document);
  if (!match?.[1]) {
    throw new Error('GraphQL document declares no named operation');
  }
  return match[1];
}

export const CREATE_LEXICON = `
  mutation createLexicon($id: ID!, $schemaId: ID!, $data: JSONData!) {
    createStructuredData(input: { id: $id, schemaId: $schemaId, data: $data }) {
      id
      data
      createdDateTime
      modifiedDateTime
    }
  }
`;

export interface CreateLexiconResponse {
  createStructuredData?: {
    id: string;
    data: unknown;
    createdDateTime: string;
    modifiedDateTime: string;
  } | null;
}

/** Replaces `data` wholesale — there is no patch mutation. See the file header. */
/**
 * The entries half of a lexicon, created.
 *
 * Identical to `CREATE_LEXICON` except for the operation name, and that is the
 * whole point: a lexicon is written as TWO records, and when only one of them
 * lands, the platform log is where you find out which. `createLexicon` and
 * `createLexiconEntries` are distinguishable there; one document used twice
 * would not be.
 */
export const CREATE_LEXICON_ENTRIES = `
  mutation createLexiconEntries($id: ID!, $schemaId: ID!, $data: JSONData!) {
    createStructuredData(input: { id: $id, schemaId: $schemaId, data: $data }) {
      id
      data
      createdDateTime
      modifiedDateTime
    }
  }
`;

export const UPDATE_LEXICON = `
  mutation updateLexicon($id: ID!, $schemaId: ID!, $data: JSONData!) {
    updateStructuredData(input: { id: $id, schemaId: $schemaId, data: $data }) {
      id
      data
      createdDateTime
      modifiedDateTime
    }
  }
`;

export interface UpdateLexiconResponse {
  updateStructuredData?: {
    id: string;
    data: unknown;
    createdDateTime: string;
    modifiedDateTime: string;
  } | null;
}

/**
 * Hard delete (BR-5).
 *
 * Deliberately not a soft delete: "delete" keeps meaning delete, which is the
 * better fit for CJIS change control. Documents already redacted with this
 * lexicon are unaffected because each applied detection embeds its own
 * `LexiconEntrySnapshot` and never dereferences a live lexicon (U5-FR2).
 */
/** The entries half, updated. Distinct from `UPDATE_LEXICON` for the reason
 * `CREATE_LEXICON_ENTRIES` is distinct from `CREATE_LEXICON`. */
export const UPDATE_LEXICON_ENTRIES = `
  mutation updateLexiconEntries($id: ID!, $schemaId: ID!, $data: JSONData!) {
    updateStructuredData(input: { id: $id, schemaId: $schemaId, data: $data }) {
      id
      data
      modifiedDateTime
    }
  }
`;

export const DELETE_LEXICON = `
  mutation deleteLexicon($id: ID!, $schemaId: ID!) {
    deleteStructuredData(input: { id: $id, schemaId: $schemaId }) {
      id
    }
  }
`;

export interface DeleteLexiconResponse {
  deleteStructuredData?: { id: string } | null;
}

/**
 * Emits one audit event.
 *
 * Note there is no `tdoId` argument: `emitAuditEvent` takes an application and
 * a free-form JSON payload. That is why EMITTING an org-scoped lexicon-mutation
 * event is straightforward — and why READING it back is the hard part, since
 * the query path filters on payload fields the application's registered audit
 * schema indexes. See `auditEmitter.ts` and open item S-1.
 */
export const EMIT_AUDIT_EVENT = `
  mutation emitLexiconAuditEvent($payload: JSONData!, $application: String) {
    emitAuditEvent(input: { application: $application, payload: $payload }) {
      id
    }
  }
`;

export interface EmitAuditEventResponse {
  emitAuditEvent?: { id: string } | null;
}
